/**
 * XJC Calibration card mode.
 *
 * Physical card references measured from 24 Colour Codes.qtx on 2026-09-24.
 * Datacolor SF600, d/8, SAV, SCI, UV Inc.
 */
(function initCalibrationCard(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.STICalibrationCard = api;
}(typeof window !== 'undefined' ? window : globalThis, function calibrationCardFactory() {
  'use strict';

  const PATCH_IDS = [
    'T-350', 'T-803', 'T-767', 'T-1151', 'T-343', 'T-1495',
    'T-708', 'T-436', 'T-147', 'T-572', 'T-775', 'T-785',
    'T-892', 'T-244', 'T-253', 'T-252', 'T-608', 'T-842',
    'T-630', 'T-517', 'T-510', 'T-620', 'T-751', 'T-551',
  ];

  const MEASURED_LAB = Object.freeze({
    'T-350': Object.freeze({ L: 92.1953, a: 0.3468, b: 3.3043 }),
    'T-803': Object.freeze({ L: 85.0759, a: -0.1044, b: 1.5525 }),
    'T-767': Object.freeze({ L: 63.8976, a: -0.2312, b: 3.1901 }),
    'T-1151': Object.freeze({ L: 54.5615, a: 0.6276, b: -0.0586 }),
    'T-343': Object.freeze({ L: 35.6599, a: 0.6077, b: 0.0916 }),
    'T-1495': Object.freeze({ L: 22.5687, a: -0.4131, b: -0.653 }),
    'T-708': Object.freeze({ L: 64.5692, a: 51.1893, b: 2.0088 }),
    'T-436': Object.freeze({ L: 37.7482, a: 50.9646, b: 1.9518 }),
    'T-147': Object.freeze({ L: 68.6136, a: 29.0662, b: 26.2338 }),
    'T-572': Object.freeze({ L: 46.2424, a: 38.4395, b: 36.6668 }),
    'T-775': Object.freeze({ L: 81.0163, a: 5.2786, b: 50.8187 }),
    'T-785': Object.freeze({ L: 55.7653, a: 11.0604, b: 47.5355 }),
    'T-892': Object.freeze({ L: 69.8947, a: -27.9676, b: 43.0642 }),
    'T-244': Object.freeze({ L: 44.2951, a: -13.4693, b: 20.8185 }),
    'T-253': Object.freeze({ L: 65.898, a: -28.9735, b: 11.0241 }),
    'T-252': Object.freeze({ L: 41.6666, a: -39.5038, b: 14.5016 }),
    'T-608': Object.freeze({ L: 63.6809, a: -25.2325, b: -11.2535 }),
    'T-842': Object.freeze({ L: 45.7818, a: -26.9377, b: -10.61 }),
    'T-630': Object.freeze({ L: 66.247, a: -9.688, b: -19.0651 }),
    'T-517': Object.freeze({ L: 39.7813, a: -11.562, b: -29.4456 }),
    'T-510': Object.freeze({ L: 52.61, a: 6.4849, b: -33.2576 }),
    'T-620': Object.freeze({ L: 38.9354, a: 8.7744, b: -30.4668 }),
    'T-751': Object.freeze({ L: 63.5115, a: 21.663, b: -23.3147 }),
    'T-551': Object.freeze({ L: 43.0531, a: 27.15, b: -20.5649 }),
  });

  const CARD = Object.freeze({
    name: 'Calibration card mode',
    version: 'MEASURED-2026-09-24',
    measured: true,
    rows: 4,
    cols: 6,
    patchIds: Object.freeze(PATCH_IDS.slice()),
    labOverrides: MEASURED_LAB,
  });

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function median(values) {
    if (!values.length) return NaN;
    const sorted = values.slice().sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2
      ? sorted[middle]
      : (sorted[middle - 1] + sorted[middle]) / 2;
  }

  function solveLinearSystem(matrix, vector) {
    const size = vector.length;
    const augmented = matrix.map((row, index) => row.slice().concat(vector[index]));

    for (let col = 0; col < size; col += 1) {
      let pivotRow = col;
      for (let row = col + 1; row < size; row += 1) {
        if (Math.abs(augmented[row][col]) > Math.abs(augmented[pivotRow][col])) pivotRow = row;
      }
      if (Math.abs(augmented[pivotRow][col]) < 1e-12) {
        throw new Error('Calibration geometry is singular. Reposition the card points.');
      }
      if (pivotRow !== col) [augmented[col], augmented[pivotRow]] = [augmented[pivotRow], augmented[col]];

      const pivot = augmented[col][col];
      for (let item = col; item <= size; item += 1) augmented[col][item] /= pivot;
      for (let row = 0; row < size; row += 1) {
        if (row === col) continue;
        const factor = augmented[row][col];
        for (let item = col; item <= size; item += 1) {
          augmented[row][item] -= factor * augmented[col][item];
        }
      }
    }

    return augmented.map(row => row[size]);
  }

  function homographyFromPoints(source, corners) {
    const matrix = [];
    const vector = [];
    source.forEach(([u, v], index) => {
      const point = corners[index];
      matrix.push([u, v, 1, 0, 0, 0, -u * point.x, -v * point.x]);
      vector.push(point.x);
      matrix.push([0, 0, 0, u, v, 1, -u * point.y, -v * point.y]);
      vector.push(point.y);
    });
    return solveLinearSystem(matrix, vector).concat(1);
  }

  function homographyFromCorners(corners) {
    if (!Array.isArray(corners) || corners.length !== 4) {
      throw new Error('Four corner patch centres are required.');
    }
    corners.forEach(point => {
      if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
        throw new Error('Every card point must contain finite x and y coordinates.');
      }
    });
    const turns = corners.map((point, index) => {
      const next = corners[(index + 1) % corners.length];
      const after = corners[(index + 2) % corners.length];
      return (next.x - point.x) * (after.y - next.y)
        - (next.y - point.y) * (after.x - next.x);
    });
    const hasClockwiseTurn = turns.some(turn => turn < -1e-6);
    const hasCounterClockwiseTurn = turns.some(turn => turn > 1e-6);
    if (turns.some(turn => Math.abs(turn) <= 1e-6) || (hasClockwiseTurn && hasCounterClockwiseTurn)) {
      throw new Error('Card corners are crossed. Mark them around the card in the shown order.');
    }
    const source = [[0, 0], [CARD.cols - 1, 0], [CARD.cols - 1, CARD.rows - 1], [0, CARD.rows - 1]];
    return homographyFromPoints(source, corners);
  }

  function projectPoint(homography, u, v) {
    const denominator = homography[6] * u + homography[7] * v + homography[8];
    if (Math.abs(denominator) < 1e-10) throw new Error('Card projection is outside the image.');
    return {
      x: (homography[0] * u + homography[1] * v + homography[2]) / denominator,
      y: (homography[3] * u + homography[4] * v + homography[5]) / denominator,
    };
  }

  function projectGrid(corners) {
    const homography = homographyFromCorners(corners);
    return CARD.patchIds.map((id, index) => {
      const row = Math.floor(index / CARD.cols);
      const col = index % CARD.cols;
      const point = projectPoint(homography, col, row);
      return { id, index, row, col, x: point.x, y: point.y };
    });
  }

  function patchCornersFromMarkers(markers) {
    if (!Array.isArray(markers) || markers.length !== 4 ||
        markers.some((marker, index) => marker.id !== index ||
          !Array.isArray(marker.corners) || marker.corners.length !== 4 ||
          marker.corners.some(point => !Number.isFinite(point.x) || !Number.isFinite(point.y)))) {
      throw new Error('All four distinct card markers are required.');
    }
    // Outermost marker corners, in millimetres from the proof's top-left edge.
    const cardCorners = [[2, 2], [122, 2], [122, 102], [2, 102]];
    const imageCorners = markers.map((marker, index) => marker.corners[index]);
    const homography = homographyFromPoints(cardCorners, imageCorners);
    const patchPositions = [[18.25, 33.25], [105.75, 33.25], [105.75, 85.75], [18.25, 85.75]];
    const corners = patchPositions.map(([x, y]) => projectPoint(homography, x, y));
    homographyFromCorners(corners);
    return corners;
  }

  function pointDistance(first, second) {
    return Math.hypot(first.x - second.x, first.y - second.y);
  }

  function assessPerspective(corners) {
    homographyFromCorners(corners);
    const top = pointDistance(corners[0], corners[1]);
    const right = pointDistance(corners[1], corners[2]);
    const bottom = pointDistance(corners[2], corners[3]);
    const left = pointDistance(corners[3], corners[0]);
    const edgeRatio = Math.max(top / bottom, bottom / top, left / right, right / left);
    return {
      edgeRatio,
      level: edgeRatio >= 1.5 ? 'strong' : edgeRatio >= 1.25 ? 'notice' : 'normal',
    };
  }

  function gridSpacing(points) {
    const distances = [];
    points.forEach(point => {
      if (point.col < CARD.cols - 1) distances.push(pointDistance(point, points[point.index + 1]));
      if (point.row < CARD.rows - 1) distances.push(pointDistance(point, points[point.index + CARD.cols]));
    });
    return Math.min(...distances);
  }

  function validateGrid(corners, width, height) {
    const points = projectGrid(corners);
    const spacing = gridSpacing(points);
    if (!Number.isFinite(spacing) || spacing < 10) {
      throw new Error('The card is too small in the photo. Move closer and try again.');
    }
    if (points.some(point => point.x < 0 || point.y < 0 || point.x >= width || point.y >= height)) {
      throw new Error('Part of the card is outside the photo. Retake with the full card visible.');
    }
    return { points, spacing };
  }

  function sampleCardPatches(imageData, corners, radiusScale) {
    if (!imageData || !imageData.data || !imageData.width || !imageData.height) {
      throw new Error('Image pixel data is required.');
    }
    const { points, spacing } = validateGrid(corners, imageData.width, imageData.height);
    const radius = Math.max(2, Math.floor(spacing * (Number.isFinite(radiusScale) ? radiusScale : 0.16)));
    const { data, width, height } = imageData;

    return points.map(point => {
      const red = [];
      const green = [];
      const blue = [];
      let channelClippedPixels = 0;
      let total = 0;
      const minX = Math.max(0, Math.floor(point.x - radius));
      const maxX = Math.min(width - 1, Math.ceil(point.x + radius));
      const minY = Math.max(0, Math.floor(point.y - radius));
      const maxY = Math.min(height - 1, Math.ceil(point.y + radius));

      for (let y = minY; y <= maxY; y += 1) {
        for (let x = minX; x <= maxX; x += 1) {
          const dx = x - point.x;
          const dy = y - point.y;
          if (dx * dx + dy * dy > radius * radius) continue;
          const offset = (y * width + x) * 4;
          const r = data[offset];
          const g = data[offset + 1];
          const b = data[offset + 2];
          red.push(r); green.push(g); blue.push(b);
          if (r <= 3 || g <= 3 || b <= 3 || r >= 252 || g >= 252 || b >= 252) {
            channelClippedPixels += 1;
          }
          total += 1;
        }
      }

      if (total < 9) {
        return { ...point, r: 0, g: 0, b: 0, radius, sampleCount: total, clipped: true };
      }
      const r = Math.round(median(red));
      const g = Math.round(median(green));
      const b = Math.round(median(blue));
      return {
        ...point,
        r, g, b,
        radius,
        sampleCount: total,
        clipped: channelClippedPixels / total > 0.3 || Math.max(r, g, b) < 5 || Math.min(r, g, b) > 250,
      };
    });
  }

  function srgbToLinear(value) {
    const normal = clamp(value / 255, 0, 1);
    return normal > 0.04045
      ? Math.pow((normal + 0.055) / 1.055, 2.4)
      : normal / 12.92;
  }

  function linearToSrgb(value) {
    const normal = clamp(value, 0, 1);
    const encoded = normal <= 0.0031308
      ? 12.92 * normal
      : 1.055 * Math.pow(normal, 1 / 2.4) - 0.055;
    return Math.round(clamp(encoded, 0, 1) * 255);
  }

  function rgbToXyz(rgb) {
    const r = srgbToLinear(rgb.r);
    const g = srgbToLinear(rgb.g);
    const b = srgbToLinear(rgb.b);
    return [
      r * 0.4124564 + g * 0.3575761 + b * 0.1804375,
      r * 0.2126729 + g * 0.7151522 + b * 0.0721750,
      r * 0.0193339 + g * 0.1191920 + b * 0.9503041,
    ];
  }

  function xyzToLab(xyz) {
    const delta = 6 / 29;
    const convert = value => value > delta ** 3
      ? Math.cbrt(value)
      : value / (3 * delta ** 2) + 4 / 29;
    const fx = convert(xyz[0] / 0.95047);
    const fy = convert(xyz[1]);
    const fz = convert(xyz[2] / 1.08883);
    return { L: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
  }

  function labToXyz(lab) {
    const delta = 6 / 29;
    const invert = value => value > delta
      ? value ** 3
      : 3 * delta ** 2 * (value - 4 / 29);
    const fy = (lab.L + 16) / 116;
    const fx = fy + lab.a / 500;
    const fz = fy - lab.b / 200;
    return [0.95047 * invert(fx), invert(fy), 1.08883 * invert(fz)];
  }

  function xyzToRgb(xyz) {
    const r = 3.2404542 * xyz[0] - 1.5371385 * xyz[1] - 0.4985314 * xyz[2];
    const g = -0.969266 * xyz[0] + 1.8760108 * xyz[1] + 0.041556 * xyz[2];
    const b = 0.0556434 * xyz[0] - 0.2040259 * xyz[1] + 1.0572252 * xyz[2];
    return { r: linearToSrgb(r), g: linearToSrgb(g), b: linearToSrgb(b) };
  }

  function deltaE2000(lab1, lab2) {
    const L1 = lab1.L; const a1 = lab1.a; const b1 = lab1.b;
    const L2 = lab2.L; const a2 = lab2.a; const b2 = lab2.b;
    const C1 = Math.hypot(a1, b1); const C2 = Math.hypot(a2, b2);
    const Cbar = (C1 + C2) / 2;
    const G = 0.5 * (1 - Math.sqrt(Cbar ** 7 / (Cbar ** 7 + 25 ** 7)));
    const a1p = (1 + G) * a1; const a2p = (1 + G) * a2;
    const C1p = Math.hypot(a1p, b1); const C2p = Math.hypot(a2p, b2);
    const hue = (a, b) => (Math.atan2(b, a) * 180 / Math.PI + 360) % 360;
    const h1p = C1p ? hue(a1p, b1) : 0; const h2p = C2p ? hue(a2p, b2) : 0;
    const dLp = L2 - L1; const dCp = C2p - C1p;
    const rawHue = h2p - h1p;
    let dhp = 0;
    if (C1p * C2p !== 0) {
      if (Math.abs(rawHue) <= 180) dhp = rawHue;
      else dhp = rawHue > 180 ? rawHue - 360 : rawHue + 360;
    }
    const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin(dhp * Math.PI / 360);
    const Lbarp = (L1 + L2) / 2; const Cbarp = (C1p + C2p) / 2;
    let hbarp;
    if (C1p * C2p === 0) hbarp = h1p + h2p;
    else if (Math.abs(h1p - h2p) <= 180) hbarp = (h1p + h2p) / 2;
    else if (h1p + h2p < 360) hbarp = (h1p + h2p + 360) / 2;
    else hbarp = (h1p + h2p - 360) / 2;
    const radians = degrees => degrees * Math.PI / 180;
    const T = 1 - 0.17 * Math.cos(radians(hbarp - 30))
      + 0.24 * Math.cos(radians(2 * hbarp))
      + 0.32 * Math.cos(radians(3 * hbarp + 6))
      - 0.20 * Math.cos(radians(4 * hbarp - 63));
    const dTheta = 30 * Math.exp(-(((hbarp - 275) / 25) ** 2));
    const Rc = 2 * Math.sqrt(Cbarp ** 7 / (Cbarp ** 7 + 25 ** 7));
    const Sl = 1 + 0.015 * (Lbarp - 50) ** 2 / Math.sqrt(20 + (Lbarp - 50) ** 2);
    const Sc = 1 + 0.045 * Cbarp; const Sh = 1 + 0.015 * Cbarp * T;
    const Rt = -Math.sin(radians(2 * dTheta)) * Rc;
    const dl = dLp / Sl; const dc = dCp / Sc; const dh = dHp / Sh;
    return Math.sqrt(dl ** 2 + dc ** 2 + dh ** 2 + Rt * dc * dh);
  }

  function fitAffine(rows) {
    const featureCount = 4;
    const normal = Array.from({ length: featureCount }, () => Array(featureCount).fill(0));
    const targets = Array.from({ length: 3 }, () => Array(featureCount).fill(0));

    rows.forEach(row => {
      const features = [
        srgbToLinear(row.observed.r),
        srgbToLinear(row.observed.g),
        srgbToLinear(row.observed.b),
        1,
      ];
      const xyz = labToXyz(row.reference);
      for (let i = 0; i < featureCount; i += 1) {
        for (let j = 0; j < featureCount; j += 1) normal[i][j] += features[i] * features[j];
        for (let channel = 0; channel < 3; channel += 1) targets[channel][i] += features[i] * xyz[channel];
      }
    });
    for (let index = 0; index < featureCount - 1; index += 1) normal[index][index] += 1e-8;
    normal[featureCount - 1][featureCount - 1] += 1e-10;
    return targets.map(target => solveLinearSystem(normal, target));
  }

  function predictXyz(rgb, matrix) {
    const features = [srgbToLinear(rgb.r), srgbToLinear(rgb.g), srgbToLinear(rgb.b), 1];
    return matrix.map(row => row.reduce((sum, coefficient, index) => sum + coefficient * features[index], 0));
  }

  function residualsForRows(rows, matrix) {
    return rows.map(row => ({
      ...row,
      deltaE: deltaE2000(xyzToLab(predictXyz(row.observed, matrix)), row.reference),
    }));
  }

  function calibrate(observedPatches, referencePatches) {
    const referenceById = new Map(referencePatches.map(patch => [patch.id, patch]));
    let rows = observedPatches
      .filter(patch => !patch.clipped && referenceById.has(patch.id))
      .map(patch => ({ observed: patch, reference: referenceById.get(patch.id) }));
    if (rows.length < 18) throw new Error('Calibration needs at least 18 usable patches.');

    let matrix = fitAffine(rows);
    let residuals = residualsForRows(rows, matrix);
    const firstMedian = median(residuals.map(row => row.deltaE));
    const threshold = Math.max(4, firstMedian * 2.5);
    const filtered = residuals.filter(row => row.deltaE <= threshold);
    if (filtered.length >= 18 && filtered.length < rows.length) {
      rows = filtered;
      matrix = fitAffine(rows);
      residuals = residualsForRows(rows, matrix);
    }

    const deltas = residuals.map(row => row.deltaE);
    const medianDeltaE = median(deltas);
    const maxDeltaE = Math.max(...deltas);
    const quality = medianDeltaE <= 3 ? 'good' : medianDeltaE <= 6 ? 'fair' : 'weak';
    return {
      matrix,
      quality,
      medianDeltaE,
      maxDeltaE,
      validPatchCount: rows.length,
      rejectedPatchCount: observedPatches.length - rows.length,
      validPatchIds: rows.map(row => row.observed.id),
      version: CARD.version,
      measured: CARD.measured,
    };
  }

  function applyCalibration(rgb, calibration) {
    if (!calibration || !Array.isArray(calibration.matrix)) throw new Error('A valid calibration is required.');
    const xyz = predictXyz(rgb, calibration.matrix).map(value => clamp(value, 0, 1.25));
    return { xyz, lab: xyzToLab(xyz), rgb: xyzToRgb(xyz) };
  }

  function parseHex(hex) {
    const clean = String(hex || '').replace('#', '');
    if (!/^[0-9a-f]{6}$/i.test(clean)) throw new Error(`Invalid colour HEX: ${hex}`);
    return {
      r: parseInt(clean.slice(0, 2), 16),
      g: parseInt(clean.slice(2, 4), 16),
      b: parseInt(clean.slice(4, 6), 16),
    };
  }

  function buildThreadDatabase(colours, fallbackRgbToLab) {
    return (colours || []).map(colour => {
      if ([colour.L, colour.a, colour.b].every(Number.isFinite)) return { ...colour };
      const fallback = fallbackRgbToLab(parseHex(colour.hex));
      return { ...colour, L: fallback.L, a: fallback.a, b: fallback.b };
    });
  }

  function getReferencePatches(colours) {
    const byId = new Map((colours || []).map(colour => [colour.id, colour]));
    return CARD.patchIds.map(id => {
      const override = CARD.labOverrides[id];
      const source = override || byId.get(id);
      if (!source || ![source.L, source.a, source.b].every(Number.isFinite)) {
        throw new Error(`Missing QTX reference for ${id}.`);
      }
      return { id, L: source.L, a: source.a, b: source.b };
    });
  }

  function rgbToLab(rgb) {
    return xyzToLab(rgbToXyz(rgb));
  }

  return Object.freeze({
    CARD,
    projectGrid,
    patchCornersFromMarkers,
    validateGrid,
    assessPerspective,
    sampleCardPatches,
    calibrate,
    applyCalibration,
    buildThreadDatabase,
    getReferencePatches,
    rgbToLab,
    deltaE2000,
  });
}));
